import { inject, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue';
import type { InjectionKey, Ref } from 'vue';
import { envelope, isPbMessage } from './protocol';
import type { AllowedDropTargets, DropTarget, ParentToBridgeMessage } from './protocol';
import { normalizeMatrix, setCell } from './tableMatrix';
import type { BuilderStore } from './useBuilderStore';

/**
 * The single I/O channel between the shell and the canvas iframe. All
 * outgoing messages travel through send(); all incoming ones are validated
 * (source, origin, envelope) before touching the store. Created once per
 * builder instance (createBuilderBridge), injected everywhere else.
 */
export interface BuilderBridge {
    iframe: Ref<HTMLIFrameElement | null>;
    lastBlockedUrl: Ref<string | null>;
    /** Live drop target echoed by the bridge during a drag. */
    dropTarget: Ref<DropTarget | null>;
    /** Authoritative drop reply; serial bumps per dragEnd. */
    lastDrop: Ref<{ target: DropTarget | null; serial: number } | null>;
    /** Right-click inside the iframe; coordinates are iframe-viewport (unscaled). */
    contextMenuRequest: Ref<{ id: string; x: number; y: number; cell: { row: number; col: number } | null; serial: number } | null>;
    /** Right-click on the preview outside any block — page-level menu. */
    pageContextMenuRequest: Ref<{ x: number; y: number; serial: number } | null>;
    /** Bumped when the bridge forwards a Cmd+K pressed inside the iframe. */
    paletteShortcutSerial: Ref<number>;
    /** Bumped when the bridge forwards a `?` pressed inside the iframe. */
    keymapShortcutSerial: Ref<number>;
    /** Bumped on failed patches and captured theme-script errors — the
     *  shell reacts by reloading the preview. */
    patchIssueSerial: Ref<number>;
    scrollTo(id: string): void;
    patchLevel(id: string, html: string): void;
    patchRegion(html: string): void;
    dragStart(kind: 'new' | 'move', blockIds: string[], allowed: AllowedDropTargets): void;
    dragMove(x: number, y: number): void;
    dragEnd(): void;
    dragCancel(): void;
}

const BRIDGE_KEY: InjectionKey<BuilderBridge> = Symbol('builder-v2-bridge');

/** Admin theme accent for the iframe overlay; empty in unthemed harnesses. */
function themeAccent(): string | undefined {
    if (typeof document === 'undefined') {
        return undefined;
    }

    const accent = getComputedStyle(document.documentElement).getPropertyValue('--pb2-accent').trim();

    return accent === '' ? undefined : accent;
}

export function createBuilderBridge(store: BuilderStore): BuilderBridge {
    const iframe = ref<HTMLIFrameElement | null>(null);
    const lastBlockedUrl = ref<string | null>(null);
    const dropTarget = ref<DropTarget | null>(null);
    const lastDrop = ref<{ target: DropTarget | null; serial: number } | null>(null);
    const contextMenuRequest = ref<BuilderBridge['contextMenuRequest']['value']>(null);
    const pageContextMenuRequest = ref<BuilderBridge['pageContextMenuRequest']['value']>(null);
    const paletteShortcutSerial = ref(0);
    const keymapShortcutSerial = ref(0);
    const patchIssueSerial = ref(0);
    let dropSerial = 0;
    let contextMenuSerial = 0;

    /** Active inline session bookkeeping: original value for Esc restore,
     *  identity guard against late streamed commits after undo/remove. */
    let activeInline: { id: string; attr: string; original: unknown } | null = null;

    const initPayload = (): ParentToBridgeMessage => ({
        type: 'init',
        payload: {
            options: { blockLinks: true },
            labels: store.labels.value,
            inline: store.inline.value,
            tables: store.tables.value,
            accent: themeAccent(),
        },
    });

    /** Active table-cell session bookkeeping (mirror of activeInline). */
    let activeTable: { id: string; attr: string; original: unknown } | null = null;

    const send = (message: ParentToBridgeMessage): void => {
        // JSON round-trip: payloads may hold Vue reactive proxies (labels,
        // blocks), which structured clone refuses (DataCloneError)
        iframe.value?.contentWindow?.postMessage(JSON.parse(JSON.stringify(envelope(message))), window.location.origin);
    };

    const onMessage = (event: MessageEvent): void => {
        if (event.source !== iframe.value?.contentWindow || event.origin !== window.location.origin) {
            return;
        }

        if (!isPbMessage(event.data)) {
            return;
        }

        const message = event.data;

        switch (message.type) {
            case 'ready':
                store.bridgeReady.value = true;
                send(initPayload());
                send({ type: 'select', payload: { id: store.selectedId.value } });
                break;
            case 'blockClick':
                store.select((message.payload as { id: string }).id);
                break;
            case 'blockDblClick':
                // No inline map matched in the bridge — route to the panel
                // (rich text / shared blocks edit there, spec §4.2).
                store.select((message.payload as { id: string }).id);
                store.panelFocusSerial.value += 1;
                break;
            case 'blockContextMenu': {
                const payload = message.payload as {
                    id: string;
                    x: number;
                    y: number;
                    cell: { row: number; col: number } | null;
                };
                contextMenuRequest.value = { ...payload, serial: ++contextMenuSerial };
                break;
            }
            case 'pageContextMenu': {
                const payload = message.payload as { x: number; y: number };
                pageContextMenuRequest.value = { ...payload, serial: ++contextMenuSerial };
                break;
            }
            case 'inlineEditStart': {
                const payload = message.payload as { id: string; attr: string };
                const value = store.findBlock(payload.id)?.value as Record<string, unknown> | null | undefined;
                activeInline = { ...payload, original: value ? (value[payload.attr] ?? null) : null };
                break;
            }
            case 'inlineEditCommit': {
                const payload = message.payload as { id: string; attr: string; html: string; final: boolean };

                if (activeInline === null || activeInline.id !== payload.id || activeInline.attr !== payload.attr) {
                    break; // session was force-ended by a structural mutation — drop late commits
                }

                store.updateValue(payload.id, payload.attr, payload.html, 'inline');

                if (payload.final) {
                    activeInline = null;
                }

                break;
            }
            case 'inlineEditCancel': {
                const payload = message.payload as { id: string; attr: string };

                if (activeInline !== null && activeInline.id === payload.id && activeInline.attr === payload.attr) {
                    store.updateValue(payload.id, payload.attr, activeInline.original, 'inline');
                }

                activeInline = null;
                break;
            }
            case 'tableCellEditStart': {
                const payload = message.payload as { id: string; attr: string };
                const value = store.findBlock(payload.id)?.value as Record<string, unknown> | null | undefined;
                const original = value ? (value[payload.attr] ?? null) : null;
                activeTable = {
                    id: payload.id,
                    attr: payload.attr,
                    original: original === null ? null : JSON.parse(JSON.stringify(original)),
                };
                break;
            }
            case 'tableCellCommit': {
                const payload = message.payload as {
                    id: string;
                    attr: string;
                    row: number;
                    col: number;
                    text: string;
                    final: boolean;
                };

                if (activeTable === null || activeTable.id !== payload.id || activeTable.attr !== payload.attr) {
                    break; // session force-ended by a structural mutation — drop late commits
                }

                const raw = (store.findBlock(payload.id)?.value ?? {})[payload.attr];
                const matrix = setCell(normalizeMatrix(raw), payload.row, payload.col, payload.text);
                // Origin 'inline': the iframe DOM already shows the typed text —
                // a patchLevel here would kill the active contenteditable.
                store.updateValue(payload.id, payload.attr, matrix, 'inline');

                if (payload.final) {
                    activeTable = null;
                }

                break;
            }
            case 'tableCellEditCancel': {
                const payload = message.payload as { id: string; attr: string };

                if (activeTable !== null && activeTable.id === payload.id && activeTable.attr === payload.attr) {
                    store.updateValue(payload.id, payload.attr, activeTable.original, 'inline');
                }

                activeTable = null;
                break;
            }
            case 'shortcut':
                if ((message.payload as { name: string }).name === 'keymap') {
                    keymapShortcutSerial.value += 1;
                } else {
                    paletteShortcutSerial.value += 1;
                }

                break;
            case 'blockHover':
                store.hoverId.value = (message.payload as { id: string | null }).id;
                break;
            case 'rects': {
                const payload = message.payload as {
                    rects: Record<string, { top: number; left: number; width: number; height: number }>;
                    documentHeight: number;
                };
                store.rects.value = payload.rects;
                store.documentHeight.value = payload.documentHeight;
                break;
            }
            case 'patched': {
                // structural swap replaced the DOM — re-apply the selection
                send({ type: 'select', payload: { id: store.selectedId.value } });

                const payload = message.payload as { ok: boolean; scriptErrors?: string[] };

                if (!payload.ok || (payload.scriptErrors && payload.scriptErrors.length)) {
                    patchIssueSerial.value += 1;
                }

                break;
            }
            case 'patchScriptError':
                patchIssueSerial.value += 1;
                break;
            case 'navigateBlocked':
                lastBlockedUrl.value = (message.payload as { url: string }).url;
                break;
            case 'dropTarget':
                dropTarget.value = (message.payload as { target: DropTarget | null }).target;
                break;
            case 'drop':
                lastDrop.value = {
                    target: (message.payload as { target: DropTarget | null }).target,
                    serial: ++dropSerial,
                };
                break;
            default:
                break;
        }
    };

    onMounted(() => window.addEventListener('message', onMessage));
    onBeforeUnmount(() => window.removeEventListener('message', onMessage));

    watch(store.selectedId, (id) => send({ type: 'select', payload: { id } }));
    // Echoing a bridge-originated hover back is harmless (idempotent set)
    watch(store.hoverId, (id) => send({ type: 'hover', payload: { id } }));
    watch([store.labels, store.inline, store.tables], () => {
        if (store.bridgeReady.value) {
            send(initPayload());
        }
    });
    // Undo/remove during typing: the bridge session dies with the patch;
    // any commit still in flight must not resurrect the reverted value.
    watch(store.lastMutation, (mutation) => {
        if (mutation !== null && mutation.kind === 'structure') {
            activeInline = null;
            activeTable = null;
        }
    });

    const bridge: BuilderBridge = {
        iframe,
        lastBlockedUrl,
        dropTarget,
        lastDrop,
        contextMenuRequest,
        pageContextMenuRequest,
        paletteShortcutSerial,
        keymapShortcutSerial,
        patchIssueSerial,
        scrollTo(id) {
            send({ type: 'scrollTo', payload: { id } });
        },
        patchLevel(id, html) {
            send({ type: 'patchLevel', payload: { id, html } });
        },
        patchRegion(html) {
            send({ type: 'patchRegion', payload: { html } });
        },
        dragStart(kind, blockIds, allowed) {
            dropTarget.value = null;
            send({ type: 'dragStart', payload: { kind, blockIds, allowed } });
        },
        dragMove(x, y) {
            send({ type: 'dragMove', payload: { x, y } });
        },
        dragEnd() {
            send({ type: 'dragEnd', payload: {} });
        },
        dragCancel() {
            dropTarget.value = null;
            send({ type: 'dragCancel', payload: {} });
        },
    };

    provide(BRIDGE_KEY, bridge);

    return bridge;
}

export function useBuilderBridge(): BuilderBridge {
    const bridge = inject(BRIDGE_KEY);

    if (!bridge) {
        throw new Error('useBuilderBridge must be used inside a BuilderV2 tree');
    }

    return bridge;
}
